import { UzButton, UzEmptyState, UzSkeleton } from '@utazzo/ui';
import { RefreshCw, Star, StarHalf } from 'lucide-react';
import React from 'react';

import { useCustomerPdfPublicContent } from '../use-customer-pdf';

/**
 * Live preview of the customer share-page section (page 2 of the FIT docket
 * PDF), rendered from the same public payload the website consumes - what
 * you see here is exactly what the customer sees. Styling mirrors
 * `apps/website/components/share/ShareLegacyAwards.tsx` with the website's
 * marketing colors inlined (navy #093f51, highlight #FFE72E, band #E8F7FC).
 */

const NAVY = '#093f51';

/** Thin rule with a filled dot at each end - the PDF's heading underline. */
const DotRule = (): React.JSX.Element => (
  <span aria-hidden className="flex items-center">
    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#093f51]" />
    <span className="h-px w-24 bg-[#093f51]/70" />
    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#093f51]" />
  </span>
);

/** Gold star row for a 0-5 rating, rounded to the nearest half. */
const StarRow = ({ rating }: { rating: number }): React.JSX.Element => {
  const half = Math.round(rating * 2) / 2;
  return (
    <span className="inline-flex items-center" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }, (_, i) => {
        if (i + 1 <= half) {
          return <Star key={i} className="h-3.5 w-3.5 fill-[#FFC107] text-[#FFC107]" />;
        }
        if (i + 0.5 === half) {
          return (
            <span key={i} className="relative inline-flex h-3.5 w-3.5">
              <Star className="absolute h-3.5 w-3.5 text-[#FFC107]" />
              <StarHalf className="absolute h-3.5 w-3.5 fill-[#FFC107] text-[#FFC107]" />
            </span>
          );
        }
        return <Star key={i} className="h-3.5 w-3.5 text-[#FFC107]" />;
      })}
    </span>
  );
};

export const CustomerPdfPreview = (): React.JSX.Element => {
  const { data: content, isLoading, refetch, isFetching } = useCustomerPdfPublicContent();

  if (isLoading) return <UzSkeleton className="h-[32rem] w-full rounded-xl" />;

  const celebrities = content?.celebrities ?? [];
  const awards = content?.awards ?? [];
  const partnerLogos = content?.partnerLogos ?? [];
  const stats = content?.stats ?? [];
  const hasAwardsOrPartners = awards.length > 0 || partnerLogos.length > 0 || stats.length > 0;

  if (celebrities.length === 0 && !hasAwardsOrPartners) {
    return (
      <UzEmptyState
        title="Nothing to preview yet"
        description="Add celebrities, awards, partner logos or stats in the other tabs - the customer page section will appear here."
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-zinc-500">
          This is exactly what customers see on the shared itinerary page.
        </p>
        <UzButton variant="ghost" size="sm" onClick={() => refetch()} loading={isFetching}>
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Refresh
        </UzButton>
      </div>

      {/* Page mock */}
      <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm">
        <div className="mx-auto max-w-[52rem] px-6 py-10 sm:px-10">
          {celebrities.length > 0 && (
            <>
              {/* Heading row with the brand logo top-right, as on the PDF page. */}
              <div className="mb-10 flex items-start justify-between gap-4">
                <div>
                  <span
                    className="inline-block rounded-xl bg-[#FFE72E] px-5 py-1.5 text-3xl font-extrabold tracking-tight sm:text-4xl"
                    style={{ color: NAVY }}
                  >
                    A Legacy of
                  </span>
                  <div className="mt-2 flex items-center gap-4">
                    <span
                      className="text-2xl font-extrabold tracking-tight sm:text-3xl"
                      style={{ color: NAVY }}
                    >
                      Trust &amp; Excellence
                    </span>
                    <DotRule />
                  </div>
                </div>
                <img
                  src="/logo.svg"
                  alt="Utazzo Holidays"
                  className="mt-1 h-8 w-auto shrink-0 sm:h-9"
                />
              </div>
              <div className="grid grid-cols-1 gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
                {celebrities.slice(0, 6).map((c) => (
                  <div key={c.id} className="flex flex-col items-center text-center">
                    {/* Top inset + top-anchored crop, matching the live share
                        page - keeps the head clear of the circle's edge instead
                        of centre-cropping a slice off the crown. */}
                    <div className="h-36 w-36 overflow-hidden rounded-full bg-[#8FD8E7] pt-[1%]">
                      {c.imageUrl && (
                        <img
                          src={c.imageUrl}
                          alt={c.name}
                          className="h-full w-full object-cover object-top"
                        />
                      )}
                    </div>
                    <h3 className="mt-4 text-lg font-extrabold sm:text-xl" style={{ color: NAVY }}>
                      {c.name}
                    </h3>
                    <p
                      className="mt-1.5 max-w-[17rem] text-[13px] leading-[18px]"
                      style={{ color: NAVY }}
                    >
                      {c.description}
                    </p>
                  </div>
                ))}
              </div>
            </>
          )}

          {hasAwardsOrPartners && (
            <div className={celebrities.length > 0 ? 'mt-16' : ''}>
              <div className="mb-6 flex items-center gap-4">
                <h2
                  className="text-3xl font-extrabold tracking-tight sm:text-4xl"
                  style={{ color: NAVY }}
                >
                  Awards &amp; Accolades
                </h2>
                <DotRule />
              </div>

              {awards.length > 0 && (
                <div className="rounded-[20px] bg-[#E8F7FC] p-5">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-3 lg:grid-cols-5">
                    {awards.map((a) => (
                      <div key={a.id} className="flex flex-col items-center text-center">
                        {a.imageUrl && (
                          <img
                            src={a.imageUrl}
                            alt={a.title}
                            className="h-20 w-auto max-w-full object-contain"
                          />
                        )}
                        <p className="mt-2 text-[11px] font-bold" style={{ color: NAVY }}>
                          {a.title}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {partnerLogos.length > 0 && (
                <>
                  <div className="mt-10 mb-6 flex items-center gap-4">
                    <h2
                      className="text-3xl font-extrabold tracking-tight sm:text-4xl"
                      style={{ color: NAVY }}
                    >
                      Destination Partners
                    </h2>
                    <DotRule />
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {partnerLogos.map((p) => (
                      <div
                        key={p.id}
                        className="inline-flex h-16 items-center justify-center rounded-xl border border-[#093f51]/20 bg-white px-4 py-2"
                      >
                        {p.imageUrl && (
                          <img
                            src={p.imageUrl}
                            alt="Partner logo"
                            className="h-full max-w-[10rem] object-contain"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {stats.length > 0 && (
                <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
                  {stats.map((s) => (
                    <div
                      key={s.id}
                      className="flex items-center gap-3 rounded-xl border border-[#093f51]/20 bg-[#E8F7FC] px-4 py-3"
                    >
                      {s.iconUrl && (
                        <img src={s.iconUrl} alt="" className="h-8 w-8 shrink-0 object-contain" />
                      )}
                      <div className="leading-tight">
                        <div className="text-sm font-extrabold" style={{ color: NAVY }}>
                          {s.value}
                        </div>
                        {s.label && (
                          <div className="text-xs" style={{ color: `${NAVY}b3` }}>
                            {s.label}
                          </div>
                        )}
                        {s.rating !== null && <StarRow rating={s.rating} />}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
